import { ApiDimension, ApiMetric } from "@incendium/api";
import { Delete, Download, Edit, Fullscreen } from "@mui/icons-material";
import {
  ClickAwayListener,
  IconButton,
  Stack,
  StackProps,
  styled,
  Tooltip,
} from "@mui/material";
import { Glass } from "Components/Glass";
import LightbulbIcon from "Components/icons/LightbulbIcon";
import Loading from "Components/Loading/Loading";
import { uniqueMetricsFromChart } from "features/analytics/utils/utils";
import { HTMLMotionProps, motion } from "motion/react";
import { IChart, TFramerAnimationProps } from "Interfaces";
import { useChartDescriptionDrawer } from "Providers/ChartDescriptionDrawerProvider";
import { useCallback, useState } from "react";
import { ReactComponent as AiIcon } from "Assets/icons/menu/ai.svg";
import { useNotification } from "Hooks";

interface IStyledAnalyticsCardToolBarWrapperProps
  extends StackProps, Pick<HTMLMotionProps<any>, TFramerAnimationProps> {
  fullscreen?: boolean;
}

const StyledAnalyticsCardToolBarWrapper = styled(
  Stack,
)<IStyledAnalyticsCardToolBarWrapperProps>(({ theme, fullscreen }) => ({
  position: fullscreen ? "relative" : "absolute",
  top: fullscreen ? 0 : 1,
  bottom: fullscreen ? 0 : 1,
  right: 0,
  background: theme.palette.primary.main,
  color: "white",
  borderTopRightRadius: theme.shape.borderRadius,
  borderBottomRightRadius: theme.shape.borderRadius,
  zIndex: 2,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: "#afb0ff",
  fill: "#afb0ff",
  height: 40,
  width: 40,
  "&:hover": {
    color: "white",
    fill: "white",
    background: "transparent",
  },
}));

interface IAnalyticsCardToolBarProps {
  chart: IChart;
  setOpen: (b: boolean) => void;
  fullscreen?: boolean;
  setFullscreen?: (b: boolean) => void;
  onEdit?: () => Promise<void>;
  onDelete?: () => Promise<void>;
  onExport?: () => Promise<void>;
  onAiInsights?: () => Promise<void>;
  aiInsightsLoading?: boolean;
  aiInsightsLoaded?: boolean;
}

function AnalyticsCardToolBar({
  chart,
  setOpen,
  fullscreen,
  setFullscreen,
  onEdit,
  onDelete,
  onExport,
  onAiInsights,
  aiInsightsLoading,
  aiInsightsLoaded,
}: IAnalyticsCardToolBarProps) {
  const { openDrawer } = useChartDescriptionDrawer();
  const { showErrorNotification } = useNotification();
  const [isProcessing, setIsProcessing] = useState<
    "export" | "edit" | "delete" | null
  >(null);

  const editChart = useCallback(async () => {
    if (!onEdit) {
      return;
    }
    setIsProcessing("edit");
    await onEdit();

    setIsProcessing(null);
  }, [onEdit]);

  const deleteChart = useCallback(async () => {
    if (!onDelete) {
      return;
    }
    setIsProcessing("delete");
    await onDelete();

    setIsProcessing(null);
  }, [onDelete]);
  const exportChart = useCallback(async () => {
    if (!onExport) {
      return;
    }
    setIsProcessing("export");
    try {
      await onExport();
    } catch (error) {
      if (error instanceof Response) {
        const t = await error.json();
        showErrorNotification(`Failed to export chart: ${t.error.message}`);
      } else {
        showErrorNotification(`Failed to export chart`);
      }
    } finally {
      setIsProcessing(null);
    }
  }, [onExport, showErrorNotification]);

  const onDescribe = useCallback(() => {
    const metrics = uniqueMetricsFromChart(chart);
    openDrawer(
      (chart.dimension || []) as ApiDimension[],
      metrics as ApiMetric[],
      chart.name && chart.name !== "" ? `${chart.name}` : undefined,
    );
  }, [chart, openDrawer]);

  const renderBody = () => {
    return (
      <StyledAnalyticsCardToolBarWrapper
        justifyContent={"flex-end"}
        py={1}
        fullscreen={fullscreen}
        component={motion.div}
        initial={{
          width: 0,
        }}
        animate={{
          width: 40,
        }}
        exit={{
          width: 0,
        }}
      >
        {onEdit && (
          <Tooltip title="Edit" placement="left-end" arrow>
            <StyledIconButton onClick={editChart}>
              <Edit />
            </StyledIconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Delete" placement="left-end" arrow>
            <StyledIconButton onClick={deleteChart}>
              <Delete />
            </StyledIconButton>
          </Tooltip>
        )}
        {setFullscreen && (
          <Tooltip title="Enlarge" placement="left-end" arrow>
            <StyledIconButton onClick={() => setFullscreen(!fullscreen)}>
              <Fullscreen />
            </StyledIconButton>
          </Tooltip>
        )}
        {onExport && (
          <Tooltip title="Export" placement="left-end" arrow>
            <StyledIconButton onClick={exportChart}>
              <Download />
            </StyledIconButton>
          </Tooltip>
        )}

        {onAiInsights && (!aiInsightsLoaded || !fullscreen) && (
          <Tooltip title="AI Insights" placement="left-end" arrow>
            <StyledIconButton
              onClick={onAiInsights}
              disabled={aiInsightsLoading}
            >
              {aiInsightsLoading ? (
                <Loading size={30} />
              ) : (
                <AiIcon height={40} width={40} />
              )}
            </StyledIconButton>
          </Tooltip>
        )}
        <Tooltip title="View Metric Details" placement="left-end" arrow>
          <StyledIconButton onClick={onDescribe}>
            <LightbulbIcon />
          </StyledIconButton>
        </Tooltip>
      </StyledAnalyticsCardToolBarWrapper>
    );
  };

  return (
    <>
      {isProcessing && (
        <Glass
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            zIndex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Loading
            text={`${
              isProcessing === "export"
                ? "Exporting"
                : isProcessing === "edit"
                  ? "Editing"
                  : "Deleting"
            }, please wait`}
          />
        </Glass>
      )}

      {fullscreen ? (
        renderBody()
      ) : (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          {renderBody()}
        </ClickAwayListener>
      )}
    </>
  );
}

export default AnalyticsCardToolBar;
